import { useParams, Link } from "react-router-dom";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SiteFooter from "@/components/SiteFooter";
import CTAFooter from "@/components/CTAFooter";
import { MapPin, Phone, Star, HelpCircle, ChevronDown, ChevronUp, ArrowLeft, ThermometerSun, Shield, Wrench, Zap, PlusCircle, RefreshCw, Leaf, Droplets, Smartphone, Wind, Home, Building2, GlassWater } from "lucide-react";
import { useState } from "react";
import { getSuburbBySlug, getSuburbsBySlug } from "@/data/suburbContent";

const suburbServices = [
  { title: "Air Conditioning Maintenance", icon: Wrench, image: "https://airconditioningguys.com.au/uploads/ac_services/service_img1.png", link: "/services/air-conditioning-maintenance", description: "Regular maintenance to keep your system running efficiently and extend its lifespan." },
  { title: "Air Conditioning Repairs", icon: ThermometerSun, image: "https://airconditioningguys.com.au/uploads/ac_services/service_img2.png", link: "/services/air-conditioning-repairs", description: "Fast, reliable repairs for all air conditioning brands and models." },
  { title: "Air Conditioning Installation", icon: PlusCircle, image: "https://airconditioningguys.com.au/uploads/ac_services/service_img3.png", link: "/services/air-conditioning-installation", description: "Professional installation of split systems, ducted systems, and multi-head units." },
  { title: "Air Conditioning Replacements", icon: RefreshCw, image: "https://airconditioningguys.com.au/uploads/ac_services/service_img4.png", link: "/services/air-conditioning-replacements", description: "Upgrade your old system with energy-efficient air conditioning technology." },
  { title: "Rejuvenation Eco Clean", icon: Leaf, image: "https://airconditioningguys.com.au/uploads/ac_services/service_img5.png", link: "/services/rejuvenation-eco-clean", description: "Deep cleaning to remove mould, bacteria and allergens from your AC system." },
  { title: "Leak Repairs", icon: Droplets, image: "https://airconditioningguys.com.au/uploads/ac_services/service_img6.png", link: "/services/leak-repairs", description: "Expert detection and repair of refrigerant and water leaks." },
  { title: "Home Automation", icon: Smartphone, image: "https://airconditioningguys.com.au/uploads/ac_services/service_img7_001.png", link: "/services/home-automation", description: "Smart home integration — control your comfort from anywhere." },
  { title: "Corrosion Protection", icon: Shield, image: "https://airconditioningguys.com.au/uploads/ac_services/service_img8.png", link: "/services/corrosion-protection", description: "Protect your outdoor unit with professional anti-corrosion treatments." },
  { title: "Ductwork Replacement & Repairs", icon: Wind, image: "https://airconditioningguys.com.au/uploads/ac_services/service_img9.png", link: "/services/ductwork-replacement-repairs", description: "Repair or replace damaged ductwork to improve airflow and efficiency." },
  { title: "Residential", icon: Home, image: "https://airconditioningguys.com.au/uploads/ac_services/istock-1227524015-1.png", link: "/services/residential-air-conditioning", description: "Complete residential air conditioning solutions for any home." },
  { title: "Commercial", icon: Building2, image: "https://airconditioningguys.com.au/uploads/ac_services/istock-1283757853-1.png", link: "/services/commercial", description: "Tailored commercial HVAC solutions for businesses of all sizes." },
  { title: "Water Cooler", icon: GlassWater, image: "https://airconditioningguys.com.au/uploads/ac_services/watercooler.jpg", link: "/services/watercooler", description: "Supply and installation of water cooler systems for your team." },
];

const SuburbDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const suburb = getSuburbBySlug(slug || "");

  if (!suburb) {
    return (
      <div className="min-h-screen">
        <StickyHeader />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">Suburb Not Found</h1>
          <p className="text-muted-foreground mb-8">The suburb you're looking for doesn't exist in our service area.</p>
          <Link to="/areas-we-service" className="text-primary font-semibold hover:underline">← Back to Areas We Service</Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const nearbySuburbs = getSuburbsBySlug(suburb.nearbySuburbs);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Air Conditioning Services in ${suburb.name}`,
    description: suburb.metaDescription,
    provider: {
      "@type": "LocalBusiness",
      name: "ACG Southern Highlands",
      telephone: "0480 841 485",
    },
    areaServed: {
      "@type": "Place",
      name: `${suburb.name}, NSW, Australia`,
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: suburb.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  return (
    <div className="min-h-screen">
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Areas We Service", href: "/areas-we-service" }, { label: suburb.name }]} />

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      {/* Hero */}
      <section className="relative h-[350px] md:h-[450px] overflow-hidden">
        <img
          src={suburb.heroImage}
          alt={`Air conditioning services in ${suburb.name}, the Southern Highlands`}
          className="w-full h-full object-cover"
          width={1200}
          height={600}
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30 flex items-end">
          <div className="container mx-auto px-4 pb-10">
            <Link to="/areas-we-service" className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Areas We Service
            </Link>
            <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-white mb-2">
              Air Conditioning {suburb.name}
            </h1>
            <div className="flex items-center gap-3 text-white/90">
              <span className="flex items-center gap-1 text-sm bg-secondary/80 px-3 py-1 rounded-full">
                <MapPin className="w-3.5 h-3.5" />
                {suburb.distance} from CBD
              </span>
              <span className="text-sm">{suburb.region}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              Expert Air Conditioning Services in {suburb.name}
            </h2>
            {suburb.overview.map((p, i) => (
              <p key={i} className="text-muted-foreground leading-relaxed mb-4">{p}</p>
            ))}

            <h3 className="text-2xl font-bold text-foreground mt-10 mb-4">
              {suburb.name}'s Climate & Air Conditioning Needs
            </h3>
            {suburb.climate.map((p, i) => (
              <p key={i} className="text-muted-foreground leading-relaxed mb-4">{p}</p>
            ))}

            <h3 className="text-2xl font-bold text-foreground mt-10 mb-4">
              Recommended Air Conditioning Systems for {suburb.name}
            </h3>
            {suburb.acRecommendations.map((p, i) => (
              <p key={i} className="text-muted-foreground leading-relaxed mb-4">{p}</p>
            ))}

            {/* Why Choose Us */}
            <h3 className="text-2xl font-bold text-foreground mt-10 mb-4">
              Why Choose ACG Southern Highlands in {suburb.name}
            </h3>
            <div className="grid md:grid-cols-2 gap-4 my-6">
              {suburb.whyChooseUs.map((item, i) => {
                const icons = [Shield, Zap, Wrench, ThermometerSun];
                const Icon = icons[i % icons.length];
                return (
                  <div key={i} className="bg-muted rounded-xl p-5 border border-border/50">
                    <Icon className="w-5 h-5 text-secondary mb-2" />
                    <p className="text-sm text-muted-foreground leading-relaxed">{item}</p>
                  </div>
                );
              })}
            </div>
          </article>
        </div>
      </section>

      {/* Hot Places */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground mb-8">
            Popular Places in {suburb.name}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {suburb.hotPlaces.map((place) => (
              <div key={place.name} className="bg-card rounded-xl p-6 border border-border shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-5 h-5 text-secondary" />
                  <h3 className="font-bold text-card-foreground">{place.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{place.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">
            FAQs — Air Conditioning in {suburb.name}
          </h2>
          <div className="space-y-4">
            {suburb.faqs.map((faq, index) => (
              <div key={index} className="bg-card rounded-xl overflow-hidden border border-border shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="font-semibold text-card-foreground">{faq.question}</span>
                  </div>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 pt-0 bg-muted/30">
                    <p className="text-muted-foreground leading-relaxed pl-8">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Suburbs - Interlinking */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground mb-4 text-center">
            Nearby Suburbs We Service
          </h2>
          <p className="text-muted-foreground text-center mb-8">
            We also provide expert air conditioning services in these nearby suburbs.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {nearbySuburbs.map((ns) => (
              <Link
                key={ns.slug}
                to={`/areas-we-service/${ns.slug}`}
                className="bg-card rounded-xl p-5 border border-border shadow-sm hover:shadow-md hover:border-secondary/50 transition-all group"
              >
                <h3 className="font-bold text-card-foreground group-hover:text-secondary transition-colors mb-1">
                  {ns.name}
                </h3>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" />
                  {ns.distance} from CBD • {ns.region}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services in this Suburb */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-foreground mb-4 text-center">
            Our Services in {suburb.name}
          </h2>
          <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
            From installations to repairs, we provide the full range of air conditioning services in {suburb.name} and surrounding areas.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {suburbServices.map((service) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.title}
                  to={service.link}
                  className="group bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg hover:border-secondary/40 transition-all duration-300"
                >
                  <div className="w-full h-40 bg-muted overflow-hidden">
                    <img
                      src={service.image}
                      alt={`${service.title} in ${suburb.name} — professional AC service by ACG Southern Highlands`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      width={400}
                      height={160}
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-secondary flex-shrink-0" />
                      <h3 className="text-sm font-bold text-card-foreground group-hover:text-secondary transition-colors leading-tight">
                        {service.title} {suburb.name}
                      </h3>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>


      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <ThermometerSun className="w-12 h-12 text-primary-foreground mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-black text-primary-foreground uppercase mb-4">
            Need Air Conditioning in {suburb.name}?
          </h2>
          <p className="text-primary-foreground/90 max-w-2xl mx-auto mb-8">
            Get a free, no-obligation quote from the Southern Highlands' trusted air conditioning experts. Same-day service available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:0480841485" className="inline-flex items-center justify-center gap-2 bg-primary-foreground text-primary font-bold py-4 px-8 rounded-lg hover:opacity-90 transition-opacity text-lg">
              <Phone className="w-5 h-5" />
              0480 841 485
            </a>
            <Link to="/areas-we-service" className="inline-flex items-center justify-center gap-2 border-2 border-primary-foreground text-primary-foreground font-bold py-4 px-8 rounded-lg hover:bg-primary-foreground/10 transition-colors text-lg">
              View All Service Areas
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default SuburbDetailPage;
